import Vue from 'vue';
import { getCurrentInstance } from 'vue-demi';

import { HITL } from '@/api';
import { IAgent, ISettings } from '@/types';
import { migrateToV2Settings } from '@/apps/app-settings/modules/agent-status-settings';
import { useAgentsStore, useAgentsSetters } from './use-agents-store';
import { useAgentStatusActions } from './use-agent-status-actions';
import { useGetAgents } from './use-get-agents';
import { THROW_API_ERROR, UIEventBus } from '@/common/ui-event-bus';
import { SETTINGS_CATEGORIES } from '@/constants';

export const useAgentsActions = () => {
  const store = useAgentsStore();
  const { setActiveAgent, addAgentsToList } = useAgentsSetters();
  const { getAPIAgent, getAPIAgents } = useGetAgents();
  const { initializeActiveAgentStatus } = useAgentStatusActions();

  const initializeAgents = async () => {
    try {
      store.isAgentListLoading = true;
      const agents = await getAPIAgents();
      addAgentsToList(agents);
      return agents;
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
      throw error;
    } finally {
      store.isAgentListLoading = false;
    }
  };

  /** In most cases we should already have active agent requested by App Launcher */
  const initializeActiveAgent = async (args: {
    userId: string;
    multiUserId: string;
    isOnline?: boolean;
    agent?: IAgent;
  }) => {
    const { agent, userId, multiUserId, isOnline = true } = args;
    const requests: Promise<any>[] = [];

    !agent
      ? requests.push(getAPIAgent({ agentId: userId, multiUserId }))
      : setActiveAgent(agent);

    if (isOnline) {
      requests.push(initializeStatusSettings());
    }

    const responses = await Promise.all(requests);
    if (!agent) {
      setActiveAgent(responses[0]);
    }

    if (isOnline) {
      await initializeActiveAgentStatus();
    }

    store.isActiveAgentReady = true;
  };

  const initializeStatusSettings = async () => {
    try {
      const response = await HITL.settings.getSettings({
        isCommon: true,
        categories: [SETTINGS_CATEGORIES.STATUS],
      });

      const category = response.data[SETTINGS_CATEGORIES.STATUS] as ISettings;
      if (category) {
        const settings = migrateToV2Settings(category.data);
        store.settings = settings;
      }
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
      throw error;
    }
  };

  /** Request individual agent if it is not available and was not requested before */
  const requestAgentById = async (agentId: string) => {
    const isLoading = store.isLoadingAgentById[agentId];
    if (store.getAgentById(agentId) || isLoading) {
      return;
    }

    try {
      Vue.set(store.isLoadingAgentById, agentId, true);
      const agent = await getAPIAgent({ agentId });
      addAgentsToList([agent]);
    } catch (error) {
      /* If request fails add a mock agent to display at least some label */
      const vm = getCurrentInstance()!;
      console.warn(`[Agents] Agent with ID ${agentId} is not available!`);

      const email = vm.proxy.$t('hitl.common.defaultAgentName');
      const agent = { id: agentId, email, data: {} };
      addAgentsToList([agent as IAgent]);

      /* TODO: Throw an error on other error types! */
    } finally {
      Vue.set(store.isLoadingAgentById, agentId, false);
    }
  };

  return {
    initializeAgents,
    initializeActiveAgent,
    requestAgentById,
  };
};
